import { Bell, Mail, Calendar, CheckCircle, AlertTriangle, Users, Sparkles } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { notifications as toastNotifications } from '../../config/toast-notifications.js';
import type { Notifications } from '../api/generated_schemas/generated-notifications-schema.js';
import type { UserMentions } from '../api/generated_schemas/generated-user_mentions-schema.js';
import { useNotifications } from '../hooks/useNotifications.js';
import { cn } from '../lib/utils.js';

import CatchUpCardStack from './notifications/CatchUpCardStack.js';
import { Badge } from './ui/badge.js';

// Tab types for notification dropdown
type NotificationTab = 'all' | 'mentions' | 'activities';


interface NotificationBadgeProps {
  currentUserId: string;
  /** Text + hover classes for the bell button (e.g. the top bar's header colors); the icon inherits the color. */
  triggerClassName?: string;
}

/**
 * NotificationBadge Component
 *
 * Displays unread user mentions count in a badge next to user profile.
 * Clicking opens dropdown showing mention details with links to source notes.
 */
const NotificationBadge: React.FC<NotificationBadgeProps> = ({ currentUserId, triggerClassName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCatchUpOpen, setIsCatchUpOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationTab>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // All notification data logic (dual-stream fetch, merge/sort, counts, read
  // toggles, deep-link resolution, 30-second polling) lives in the shared hook
  // so this badge and the future Catch-Up card stack share ONE source of truth.
  const {
    unreadMentions,
    unreadActivityNotifications,
    items: combinedNotifications,
    mentionsCount,
    activityCount,
    unreadCount,
    refetch,
    isMutating,
    getMentionContext,
    resolveNotificationUrl,
    markMentionRead,
    markMentionUnread,
    markNotificationRead,
    markNotificationUnread,
    markAllRead,
  } = useNotifications(currentUserId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Refetch when dropdown opens
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  // Format timestamp for display
  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) {return 'Unknown time';}

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) {return 'Just now';}
    if (diffMins < 60) {return `${diffMins}m ago`;}

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {return `${diffHours}h ago`;}

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) {return `${diffDays}d ago`;}

    return date.toLocaleDateString();
  };

  // Parse mentions markup and convert to clean display text
  const cleanMentionContent = (content: string | null | undefined) => {
    if (!content) {return 'No content';}

    // Replace @[Display Name](UUID) format with just @Display Name
    return content.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');
  };

  // Truncate note content for display
  const truncateContent = (content: string | null | undefined, maxLength: number = 80) => {
    if (!content) {return 'No content';}

    // First clean the mentions, then truncate
    const cleaned = cleanMentionContent(content);
    if (cleaned.length <= maxLength) {return cleaned;}
    return `${cleaned.substring(0, maxLength)  }...`;
  };

  // Handle mention click - navigate to the lot/job and close dropdown
  const handleMentionClick = async (mention: UserMentions) => {
    const context = getMentionContext(mention);

    if (context) {
      // Mark the mention as read (hook refetches to update badge count)
      try {
        await markMentionRead(mention);
      } catch (error) {
        console.error('Error marking mention as read:', error);
      }

      // Navigate to the context location
      navigate(context.url);
      setIsOpen(false);
    } else {
      console.warn('No context found for mention:', mention);
      toastNotifications.error('Error', 'Unable to find the location of this mention');
    }
  };

  // Handle mark as unread - marks mention as unread without navigation
  const handleMarkAsUnread = async (mention: UserMentions, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent parent button click (navigation)

    try {
      await markMentionUnread(mention);
      toastNotifications.success('Success', 'Marked as unread');
    } catch (error) {
      console.error('Error marking mention as unread:', error);
      toastNotifications.error('Error', 'Failed to mark as unread');
    }
  };

  // Handle activity notification click - navigate to related entity and mark as read
  const handleActivityNotificationClick = async (notification: Notifications) => {
    // Mark the notification as read (hook refetches to update badge count)
    try {
      await markNotificationRead(notification);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }

    // Navigate to the resolved deep-link target if available
    const url = resolveNotificationUrl(notification);
    if (url) {
      navigate(url);
      setIsOpen(false);
    }
  };

  // Handle mark activity notification as unread
  const handleMarkActivityAsUnread = async (notification: Notifications, event: React.MouseEvent) => {
    event.stopPropagation();

    try {
      await markNotificationUnread(notification);
      toastNotifications.success('Success', 'Marked as unread');
    } catch (error) {
      console.error('Error marking notification as unread:', error);
      toastNotifications.error('Error', 'Failed to mark as unread');
    }
  };

  // Get icon for activity notification based on type
  const getActivityNotificationIcon = (notificationTypeCode: string | null | undefined) => {
    switch (notificationTypeCode) {
      case 'ACTIVITY_INSTALL_DATE_CHANGED_72H':
        return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case 'ACTIVITY_INSTALL_DATE_CHANGED':
        return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'ACTIVITY_STATUS_CHANGED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'ACTIVITY_ASSIGNED':
        return <Users className="w-4 h-4 text-purple-500" />;
      case 'ACTIVITY_COMPLETED':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'ACTIVITY_CREATED':
        return <Bell className="w-4 h-4 text-blue-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  // Get badge variant for activity notification importance
  const getImportanceBadgeVariant = (importance: string | null | undefined) => {
    switch (importance) {
      case 'urgent':
        return 'destructive';
      case 'high':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  if (!currentUserId) {
    return null; // Don't render if no user logged in
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button with Badge — opens the focused Catch-Up stack by
          default (the traditional list is reachable via "View as list" inside). */}
      <button
        onClick={() => setIsCatchUpOpen(true)}
        className={cn(
          "relative flex items-center justify-center p-2 rounded-lg transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
          "border border-transparent",
          triggerClassName ?? "hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-200 dark:hover:border-gray-700"
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
        title={`${unreadCount} unread mention${unreadCount !== 1 ? 's' : ''}`}
      >
        <Bell className={cn("w-5 h-5", !triggerClassName && "text-gray-600 dark:text-gray-400")} />

        {/* Unread count badge */}
        {unreadCount > 0 && (
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 h-5 min-w-5 px-1 flex items-center justify-center text-xs font-semibold"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            "absolute right-0 mt-2 w-96 bg-white dark:bg-gray-900",
            "rounded-lg shadow-xl border border-gray-200 dark:border-gray-700",
            "py-2 z-50 animate-in fade-in slide-in-from-top-1 duration-200",
            "max-h-[700px] flex flex-col"
          )}
          role="menu"
          aria-orientation="vertical"
        >
          {/* Header with Tabs */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Notifications
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {unreadCount} unread
                  </Badge>
                )}
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setIsCatchUpOpen(true);
                  }}
                  className={cn(
                    "inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium",
                    "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300",
                    "hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors",
                    "focus:outline-none focus:ring-2 focus:ring-blue-500"
                  )}
                  title="Walk through your unread notifications one at a time"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Catch Up
                </button>
              </div>
            </div>
            {/* Tab buttons */}
            <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-md">
              <button
                onClick={() => setActiveTab('all')}
                className={cn(
                  "flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors",
                  activeTab === 'all'
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                )}
              >
                All ({unreadCount})
              </button>
              <button
                onClick={() => setActiveTab('mentions')}
                className={cn(
                  "flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors",
                  activeTab === 'mentions'
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                )}
              >
                Mentions ({mentionsCount})
              </button>
              <button
                onClick={() => setActiveTab('activities')}
                className={cn(
                  "flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors",
                  activeTab === 'activities'
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                )}
              >
                Events ({activityCount})
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {/* Empty state */}
            {((activeTab === 'all' && unreadCount === 0) ||
              (activeTab === 'mentions' && mentionsCount === 0) ||
              (activeTab === 'activities' && activityCount === 0)) ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-700 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No unread {activeTab === 'mentions' ? 'mentions' : activeTab === 'activities' ? 'events' : 'notifications'}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  You&apos;re all caught up!
                </p>
              </div>
            ) : (
              <div className="py-2">
                {/* Combined view for "All" tab - sorted by timestamp */}
                {activeTab === 'all' && combinedNotifications.map((item) => {
                  if (item.type === 'notification') {
                    const notification = item.data;
                    return (
                      <div
                        key={`notification-${notification.id}`}
                        className={cn(
                          "relative group border-l-4",
                          notification.importance === 'urgent' ? 'border-red-500' :
                          notification.importance === 'high' ? 'border-orange-500' : 'border-blue-500'
                        )}
                      >
                        <button
                          onClick={() => handleActivityNotificationClick(notification)}
                          className={cn(
                            "w-full px-4 py-3 text-left",
                            "hover:bg-gray-50 dark:hover:bg-gray-800",
                            "transition-colors duration-150"
                          )}
                          role="menuitem"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                              {getActivityNotificationIcon(notification.notification_type_code)}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                                  {notification.title}
                                </p>
                                <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                                  {formatTimestamp(notification.created_at)}
                                </span>
                              </div>

                              {notification.importance && notification.importance !== 'normal' && (
                                <div className="mb-1">
                                  <Badge
                                    variant={getImportanceBadgeVariant(notification.importance) as any}
                                    className="text-xs"
                                  >
                                    {notification.importance}
                                  </Badge>
                                </div>
                              )}

                              <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                                {notification.message}
                              </p>

                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className="text-xs">
                                  event
                                </Badge>
                                {notification.action_text && (
                                  <span className="text-xs text-blue-600 dark:text-blue-400">
                                    {notification.action_text} →
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={(e) => handleMarkActivityAsUnread(notification, e)}
                          className={cn(
                            "absolute top-2 right-2 p-1.5 rounded-md",
                            "bg-white dark:bg-gray-800",
                            "border border-gray-200 dark:border-gray-700",
                            "opacity-0 group-hover:opacity-100",
                            "transition-opacity duration-150",
                            "hover:bg-gray-100 dark:hover:bg-gray-700",
                            "focus:outline-none focus:ring-2 focus:ring-blue-500",
                            "shadow-sm"
                          )}
                          title="Mark as unread"
                          aria-label="Mark as unread"
                          disabled={isMutating}
                        >
                          <Mail className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                      </div>
                    );
                  } else {
                    const mention = item.data;
                    const context = getMentionContext(mention);
                    return (
                      <div
                        key={`mention-${mention.id}`}
                        className={cn(
                          "relative group border-l-4",
                          context?.type === 'lot' ? 'border-green-500' : 'border-purple-500'
                        )}
                      >
                        <button
                          onClick={() => handleMentionClick(mention)}
                          className={cn(
                            "w-full px-4 py-3 text-left",
                            "hover:bg-gray-50 dark:hover:bg-gray-800",
                            "transition-colors duration-150"
                          )}
                          role="menuitem"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                              {mention.trig_user_full_name ?
                                mention.trig_user_full_name
                                  .split(' ')
                                  .map(n => n[0])
                                  .join('')
                                  .toUpperCase()
                                  .slice(0, 2)
                                : '??'}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {mention.trig_user_full_name || 'Unknown User'}
                                </p>
                                <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                                  {formatTimestamp(mention.mentioned_at)}
                                </span>
                              </div>

                              {context && (
                                <div className="mb-1">
                                  <Badge
                                    variant={context.type === 'lot' ? 'success' : 'default'}
                                    className="text-xs"
                                  >
                                    {context.label}
                                  </Badge>
                                  {context.subtitle && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                      {context.subtitle}
                                    </p>
                                  )}
                                </div>
                              )}

                              {mention.trig_note_title && (
                                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  {mention.trig_note_title}
                                </p>
                              )}

                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                {truncateContent(mention.trig_note_content)}
                              </p>

                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className="text-xs">
                                  {mention.mention_type || 'mention'}
                                </Badge>
                                <span className="text-xs text-blue-600 dark:text-blue-400">
                                  Click to view →
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={(e) => handleMarkAsUnread(mention, e)}
                          className={cn(
                            "absolute top-2 right-2 p-1.5 rounded-md",
                            "bg-white dark:bg-gray-800",
                            "border border-gray-200 dark:border-gray-700",
                            "opacity-0 group-hover:opacity-100",
                            "transition-opacity duration-150",
                            "hover:bg-gray-100 dark:hover:bg-gray-700",
                            "focus:outline-none focus:ring-2 focus:ring-blue-500",
                            "shadow-sm"
                          )}
                          title="Mark as unread"
                          aria-label="Mark as unread"
                          disabled={isMutating}
                        >
                          <Mail className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                      </div>
                    );
                  }
                })}

                {/* Activity Notifications - only for "Events" tab */}
                {activeTab === 'activities' && unreadActivityNotifications.map((notification) => (
                  <div
                    key={`notification-${notification.id}`}
                    className={cn(
                      "relative group border-l-4",
                      notification.importance === 'urgent' ? 'border-red-500' :
                      notification.importance === 'high' ? 'border-orange-500' : 'border-blue-500'
                    )}
                  >
                    <button
                      onClick={() => handleActivityNotificationClick(notification)}
                      className={cn(
                        "w-full px-4 py-3 text-left",
                        "hover:bg-gray-50 dark:hover:bg-gray-800",
                        "transition-colors duration-150"
                      )}
                      role="menuitem"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                          {getActivityNotificationIcon(notification.notification_type_code)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                              {notification.title}
                            </p>
                            <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                              {formatTimestamp(notification.created_at)}
                            </span>
                          </div>

                          {/* Importance badge */}
                          {notification.importance && notification.importance !== 'normal' && (
                            <div className="mb-1">
                              <Badge
                                variant={getImportanceBadgeVariant(notification.importance) as any}
                                className="text-xs"
                              >
                                {notification.importance}
                              </Badge>
                            </div>
                          )}

                          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                            {notification.message}
                          </p>

                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              event
                            </Badge>
                            {notification.action_text && (
                              <span className="text-xs text-blue-600 dark:text-blue-400">
                                {notification.action_text} →
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Mark as Unread Button - Appears on hover */}
                    <button
                      onClick={(e) => handleMarkActivityAsUnread(notification, e)}
                      className={cn(
                        "absolute top-2 right-2 p-1.5 rounded-md",
                        "bg-white dark:bg-gray-800",
                        "border border-gray-200 dark:border-gray-700",
                        "opacity-0 group-hover:opacity-100",
                        "transition-opacity duration-150",
                        "hover:bg-gray-100 dark:hover:bg-gray-700",
                        "focus:outline-none focus:ring-2 focus:ring-blue-500",
                        "shadow-sm"
                      )}
                      title="Mark as unread"
                      aria-label="Mark as unread"
                      disabled={isMutating}
                    >
                      <Mail className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    </button>
                  </div>
                ))}

                {/* User Mentions - only for "Mentions" tab */}
                {activeTab === 'mentions' && unreadMentions.map((mention) => {
                  const context = getMentionContext(mention);

                  return (
                    <div
                      key={`mention-${mention.id}`}
                      className={cn(
                        "relative group border-l-4",
                        context?.type === 'lot' ? 'border-green-500' : 'border-purple-500'
                      )}
                    >
                      <button
                        onClick={() => handleMentionClick(mention)}
                        className={cn(
                          "w-full px-4 py-3 text-left",
                          "hover:bg-gray-50 dark:hover:bg-gray-800",
                          "transition-colors duration-150"
                        )}
                        role="menuitem"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                            {mention.trig_user_full_name ?
                              mention.trig_user_full_name
                                .split(' ')
                                .map(n => n[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2)
                              : '??'}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {mention.trig_user_full_name || 'Unknown User'}
                              </p>
                              <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                                {formatTimestamp(mention.mentioned_at)}
                              </span>
                            </div>

                            {/* Context label (Lot/Job) */}
                            {context && (
                              <div className="mb-1">
                                <Badge
                                  variant={context.type === 'lot' ? 'success' : 'default'}
                                  className="text-xs"
                                >
                                  {context.label}
                                </Badge>
                                {context.subtitle && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {context.subtitle}
                                  </p>
                                )}
                              </div>
                            )}

                            {mention.trig_note_title && (
                              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                {mention.trig_note_title}
                              </p>
                            )}

                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {truncateContent(mention.trig_note_content)}
                            </p>

                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">
                                {mention.mention_type || 'mention'}
                              </Badge>
                              <span className="text-xs text-blue-600 dark:text-blue-400">
                                Click to view →
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>

                      {/* Mark as Unread Button - Appears on hover */}
                      <button
                        onClick={(e) => handleMarkAsUnread(mention, e)}
                        className={cn(
                          "absolute top-2 right-2 p-1.5 rounded-md",
                          "bg-white dark:bg-gray-800",
                          "border border-gray-200 dark:border-gray-700",
                          "opacity-0 group-hover:opacity-100",
                          "transition-opacity duration-150",
                          "hover:bg-gray-100 dark:hover:bg-gray-700",
                          "focus:outline-none focus:ring-2 focus:ring-blue-500",
                          "shadow-sm"
                        )}
                        title="Mark as unread"
                        aria-label="Mark as unread"
                        disabled={isMutating}
                      >
                        <Mail className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {unreadCount > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-800 flex-shrink-0">
              <button
                onClick={async () => {
                  try {
                    // Determine which items to mark based on active tab
                    const mentionsToMark = (activeTab === 'all' || activeTab === 'mentions') ? unreadMentions : [];
                    const notificationsToMark = (activeTab === 'all' || activeTab === 'activities') ? unreadActivityNotifications : [];

                    const totalMarked = await markAllRead({
                      mentions: mentionsToMark,
                      notifications: notificationsToMark,
                    });

                    toastNotifications.success('Success', `Marked ${totalMarked} notification${totalMarked !== 1 ? 's' : ''} as read`);
                    setIsOpen(false);
                  } catch (error) {
                    console.error('Error marking notifications as read:', error);
                    toastNotifications.error('Error', 'Failed to mark notifications as read');
                  }
                }}
                disabled={isMutating}
                className="w-full text-sm text-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMutating
                  ? 'Marking as read...'
                  : `Mark all ${activeTab === 'all' ? '' : activeTab === 'mentions' ? 'mentions ' : 'events '}as read`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Focused "Catch Up" card stack — opened from the dropdown header.
          Mounted outside the dropdown so it survives the dropdown closing. */}
      <CatchUpCardStack
        currentUserId={currentUserId}
        open={isCatchUpOpen}
        onClose={() => setIsCatchUpOpen(false)}
        onViewList={() => {
          setIsCatchUpOpen(false);
          setIsOpen(true);
        }}
      />
    </div>
  );
};

export default NotificationBadge;
